import React, { useState, useEffect } from 'react';
import { Check } from 'lucide-react';
import { toast } from 'sonner';
import Button from '../shared/Button';

interface AddTickerFormProps {
    onAdd: (ticker: string, note: string) => Promise<void>;
    initialTicker?: string;
    fixedTicker?: boolean;
}

const inputClass =
  'w-full bg-gray-50 dark:bg-[#141e30] border border-gray-200 dark:border-cat-border rounded-xl p-2.5 text-sm text-gray-900 dark:text-cat-text-main placeholder-gray-400 dark:placeholder-cat-text-dark focus:outline-none focus:border-cat-accent transition-colors';

export default function AddTickerForm({ onAdd, initialTicker = '', fixedTicker = false }: AddTickerFormProps) {
    const [ticker, setTicker] = useState<string>(initialTicker || '');
    const [note, setNote] = useState<string>('');
    const [loading, setLoading] = useState(false);
    const [uiError, setUiError] = useState<string | null>(null);
    const [successMsg, setSuccessMsg] = useState<string | null>(null);

    useEffect(() => {
        setTicker(initialTicker || '');
    }, [initialTicker]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setUiError(null);
        setSuccessMsg(null);

        if (!ticker.trim()) {
            setUiError('Please enter a valid ticker');
            return;
        }

        const createdTicker = ticker.trim().toUpperCase();
        setLoading(true);
        try {
            await onAdd(createdTicker, note);

            if (!fixedTicker) {
                setTicker('');
            }
            setNote('');

            const msg = `Added ${createdTicker} to watchlist`;
            setSuccessMsg(msg);
            toast.success(msg, { description: 'Ticker is now tracked with live quotes.' });
            setTimeout(() => setSuccessMsg(null), 4000);
        } catch (err: any) {
            const status = err?.response?.status || err?.status;
            if (status === 409 || err?.code === 'DUPLICATE') {
                setUiError(`${createdTicker} is already in your watchlist.`);
            } else if (status === 400) {
                setUiError('Ticker not found.');
            } else if (err?.message) {
                setUiError(err.message);
            } else {
                setUiError('Failed to add ticker. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="rounded-2xl border border-gray-200 dark:border-cat-border bg-white dark:bg-cat-surface shadow-sm overflow-hidden">
            <div className="h-[3px] bg-gradient-to-r from-[#10d98e] via-[#06b6d4] to-cat-accent" />
            <div className="p-5">
                <h3 className="text-[11px] font-bold uppercase tracking-[0.15em] text-gray-500 dark:text-cat-text-muted mb-4">
                    Add a ticker
                </h3>
                <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                    <div>
                        <label className="block text-xs text-gray-400 dark:text-cat-text-muted mb-1.5">Ticker Symbol</label>
                        <input
                            type="text"
                            value={ticker}
                            readOnly={fixedTicker}
                            onChange={(e) => {
                                if (fixedTicker) return;
                                setUiError(null);
                                setTicker(e.target.value.replace(/[^A-Za-z0-9.-]/g, '').toUpperCase());
                            }}
                            placeholder="e.g. NVDA"
                            className={`${inputClass} ${fixedTicker ? 'opacity-60 cursor-not-allowed bg-gray-100 dark:bg-cat-elevated' : ''}`}
                        />
                    </div>

                    {!fixedTicker && (
                        <div>
                            <label className="block text-xs text-gray-400 dark:text-cat-text-muted mb-1.5">Free Note</label>
                            <textarea
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                                placeholder="Watching for Catalyst label"
                                className={`${inputClass} h-20 resize-none`}
                            />
                        </div>
                    )}

                    {uiError && (
                        <div className="text-cat-red text-[11px] font-semibold text-center bg-[#f5476a]/10 p-2.5 rounded-xl border border-[#f5476a]/20">
                            {uiError}
                        </div>
                    )}

                    {successMsg && (
                        <div className="text-emerald-700 dark:text-cat-green text-[11px] font-semibold text-center bg-emerald-50 dark:bg-[#10d98e]/10 p-2.5 rounded-xl border border-emerald-200 dark:border-[#10d98e]/20 flex items-center justify-center gap-1.5">
                            <Check size={14} />
                            {successMsg}
                        </div>
                    )}

                    <Button type="submit" loading={loading} className="w-full" size="md">
                        {loading ? 'Adding...' : 'Add to Watchlist +'}
                    </Button>
                </form>
            </div>
        </div>
    );
}
